import { MERRILL_TICKER_MAP } from "./merrill-ticker-map.js";

/**
 * Parsers for the files people export from their banks and brokerage.
 * Everything here runs in the browser so the import screen can show
 * exactly what will change before anything is sent.
 */

/** RFC 4180 CSV, including newlines inside quoted fields (Amex uses them). */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;
  const input = text.replace(/^\uFEFF/, "");
  for (let i = 0; i < input.length; i++) {
    const c = input[i]!;
    if (quoted) {
      if (c !== '"') field += c;
      else if (input[i + 1] === '"') {
        field += '"';
        i++;
      } else quoted = false;
    } else if (c === '"') quoted = true;
    else if (c === ",") {
      row.push(field);
      field = "";
    } else if (c === "\n" || c === "\r") {
      if (c === "\r" && input[i + 1] === "\n") i++;
      row.push(field);
      field = "";
      rows.push(row);
      row = [];
    } else field += c;
  }
  if (field !== "" || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => r.some((cell) => cell.trim() !== ""));
}

/** "1,234.56", "$12.34", "-12.34", "(12.34)" to cents, without float error. */
export function moneyToCents(value: string | undefined): number | null {
  if (value === undefined) return null;
  const trimmed = value.trim();
  if (trimmed === "" || trimmed === "--") return null;
  const negative = /^-|^\(.*\)$/.test(trimmed);
  const digits = trimmed.replace(/[$,()\s-]/g, "");
  const match = /^(\d*)(?:\.(\d*))?$/.exec(digits);
  if (!match || digits === "" || digits === ".") return null;
  const fraction = (match[2] ?? "").padEnd(3, "0");
  const cents =
    Number(match[1] || "0") * 100 +
    Number(fraction.slice(0, 2)) +
    (Number(fraction[2]) >= 5 ? 1 : 0);
  return negative ? -cents : cents;
}

/** MM/DD/YYYY, MM/DD/YY or YYYY-MM-DD to YYYY-MM-DD. */
export function toIsoDate(value: string | undefined): string | null {
  const s = (value ?? "").trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/.exec(s);
  if (!m) return null;
  const year = m[3]!.length === 2 ? `20${m[3]}` : m[3]!;
  return `${year}-${m[1]!.padStart(2, "0")}-${m[2]!.padStart(2, "0")}`;
}

// === Bank and card statements ===============================================

export type StatementFormat = "amex" | "bofa_bank" | "bofa_card";
/** Transfers and deposits start unchecked: a card payment from checking
 *  is the same money as the card's charges, so counting both doubles it. */
export type StatementRowKind = "spending" | "refund" | "transfer" | "deposit";

export interface StatementRow {
  readonly date: string;
  readonly merchant: string;
  /** Positive is money spent, negative is a refund. */
  readonly amount_cents: number;
  readonly kind: StatementRowKind;
  readonly category?: string;
  /** Stable across re-imports, so the same row is never added twice. */
  readonly source_ref: string;
}

export interface ParsedStatement {
  readonly format: StatementFormat;
  readonly rows: readonly StatementRow[];
}

export const STATEMENT_LABELS: Readonly<Record<StatementFormat, string>> = {
  amex: "American Express",
  bofa_bank: "Bank of America checking or savings",
  bofa_card: "Bank of America credit card",
};

const TRANSFER_PATTERN =
  /payment\s*[-–]?\s*thank you|online payment|auto\s?pay|american express|\bamex\b|credit card|card payment|bill payment|\bepay\b|transfer|\bxfer\b|\bwire\b|merrill|fidelity|vanguard|schwab/i;

const normalize = (cell: string) => cell.trim().toLowerCase();

function findHeader(
  rows: readonly string[][],
  required: readonly string[],
): number {
  return rows.findIndex((row) => {
    const cells = row.map(normalize);
    return required.every((name) => cells.includes(name));
  });
}

export function parseStatementCsv(text: string): ParsedStatement {
  const rows = parseCsv(text);

  const cardHeader = findHeader(rows, ["posted date", "payee", "amount"]);
  if (cardHeader >= 0) return parseBofaCard(rows, cardHeader);

  const bankHeader = findHeader(rows, [
    "date",
    "description",
    "amount",
    "running bal.",
  ]);
  if (bankHeader >= 0) return parseBofaBank(rows, bankHeader);

  const amexHeader = findHeader(rows, ["date", "description", "amount"]);
  if (amexHeader >= 0) return parseAmex(rows, amexHeader);

  throw new Error(
    "This doesn't look like a Bank of America or American Express CSV export.",
  );
}

function columnIndex(header: readonly string[]) {
  const cells = header.map(normalize);
  return (name: string) => cells.indexOf(name);
}

/** Identical same-day charges are real, so number them to keep both. */
function occurrenceCounter() {
  const seen = new Map<string, number>();
  return (key: string) => {
    const n = (seen.get(key) ?? 0) + 1;
    seen.set(key, n);
    return `${key}|${n}`;
  };
}

function parseAmex(rows: readonly string[][], headerIndex: number): ParsedStatement {
  const col = columnIndex(rows[headerIndex]!);
  const [dateCol, descCol, amountCol] = [
    col("date"),
    col("description"),
    col("amount"),
  ];
  const [refCol, categoryCol] = [col("reference"), col("category")];
  const nextKey = occurrenceCounter();
  const out: StatementRow[] = [];
  for (const row of rows.slice(headerIndex + 1)) {
    const date = toIsoDate(row[dateCol]);
    const cents = moneyToCents(row[amountCol]);
    const merchant = (row[descCol] ?? "").trim();
    if (date === null || cents === null || merchant === "") continue;
    const reference = refCol >= 0 ? (row[refCol] ?? "").replace(/'/g, "").trim() : "";
    const category = categoryCol >= 0 ? (row[categoryCol] ?? "").trim() : "";
    const kind: StatementRowKind =
      cents >= 0
        ? "spending"
        : TRANSFER_PATTERN.test(merchant)
          ? "transfer"
          : "refund";
    out.push({
      date,
      merchant,
      amount_cents: cents,
      kind,
      ...(category !== "" && { category }),
      source_ref:
        reference !== ""
          ? `amex:${reference}`
          : nextKey(`amex:${date}|${cents}|${merchant}`),
    });
  }
  return { format: "amex", rows: out };
}

function parseBofaBank(
  rows: readonly string[][],
  headerIndex: number,
): ParsedStatement {
  const col = columnIndex(rows[headerIndex]!);
  const [dateCol, descCol, amountCol] = [
    col("date"),
    col("description"),
    col("amount"),
  ];
  const nextKey = occurrenceCounter();
  const out: StatementRow[] = [];
  for (const row of rows.slice(headerIndex + 1)) {
    const date = toIsoDate(row[dateCol]);
    const cents = moneyToCents(row[amountCol]);
    const merchant = (row[descCol] ?? "").trim();
    // Balance lines have a date-like description but no amount.
    if (date === null || cents === null || merchant === "") continue;
    const kind: StatementRowKind =
      cents > 0
        ? "deposit"
        : TRANSFER_PATTERN.test(merchant)
          ? "transfer"
          : "spending";
    out.push({
      date,
      merchant,
      amount_cents: -cents,
      kind,
      source_ref: nextKey(`bofa:${date}|${cents}|${merchant}`),
    });
  }
  return { format: "bofa_bank", rows: out };
}

function parseBofaCard(
  rows: readonly string[][],
  headerIndex: number,
): ParsedStatement {
  const col = columnIndex(rows[headerIndex]!);
  const [dateCol, payeeCol, amountCol, refCol] = [
    col("posted date"),
    col("payee"),
    col("amount"),
    col("reference number"),
  ];
  const nextKey = occurrenceCounter();
  const out: StatementRow[] = [];
  for (const row of rows.slice(headerIndex + 1)) {
    const date = toIsoDate(row[dateCol]);
    const cents = moneyToCents(row[amountCol]);
    const merchant = (row[payeeCol] ?? "").trim();
    if (date === null || cents === null || merchant === "") continue;
    const reference = refCol >= 0 ? (row[refCol] ?? "").trim() : "";
    const kind: StatementRowKind =
      cents < 0
        ? "spending"
        : TRANSFER_PATTERN.test(merchant)
          ? "transfer"
          : "refund";
    out.push({
      date,
      merchant,
      amount_cents: -cents,
      kind,
      source_ref:
        reference !== ""
          ? `bofa-card:${reference}`
          : nextKey(`bofa-card:${date}|${cents}|${merchant}`),
    });
  }
  return { format: "bofa_card", rows: out };
}

/** Whether a row starts checked on the import screen. */
export function includedByDefault(row: StatementRow): boolean {
  return row.kind === "spending" || row.kind === "refund";
}

// === Merrill tax lots =======================================================

export interface MerrillLot {
  readonly ticker: string;
  readonly purchase_date: string;
  readonly quantity: number;
  readonly unit_cost_cents: number;
  readonly cost_basis_cents: number;
  readonly value_cents?: number;
  readonly tax_term?: "long" | "short";
}

export interface MerrillAccountLots {
  readonly external_id: string;
  readonly nickname: string;
  readonly lots: readonly MerrillLot[];
}

export interface ParsedMerrillLots {
  readonly as_of_date: string | null;
  readonly accounts: readonly MerrillAccountLots[];
  readonly warnings: readonly string[];
}

/**
 * Merrill lists sweep funds like positions, but this money is the account's
 * cash. Importing it as a holding would count it twice.
 */
const MERRILL_SWEEP_SYMBOLS: ReadonlySet<string> = new Set(["IIAXX"]);

const MERRILL_COLUMNS = [
  "cob date",
  "symbol",
  "account #",
  "acquisition date",
  "quantity",
  "unit cost ($)",
  "cost basis ($)",
  "value ($)",
  "short/long",
] as const;

/** Merrill's "Unrealized Gain/Loss with Tax Lots" download. */
export function parseMerrillLotsCsv(text: string): ParsedMerrillLots {
  const rows = parseCsv(text);
  const headerIndex = findHeader(rows, MERRILL_COLUMNS);
  if (headerIndex < 0) {
    throw new Error(
      "This isn't Merrill's Unrealized Gain/Loss with Tax Lots CSV. Other Merrill exports leave out purchase dates.",
    );
  }
  const col = columnIndex(rows[headerIndex]!);
  const nicknameCol = col("account nickname");
  const descriptionCol = col("security description");
  const warnings: string[] = [];
  const accounts = new Map<string, { nickname: string; lots: MerrillLot[] }>();
  let asOfDate: string | null = null;

  rows.slice(headerIndex + 1).forEach((row, i) => {
    const line = headerIndex + i + 2;
    asOfDate ??= toIsoDate(row[col("cob date")]);
    const rawSymbol = (row[col("symbol")] ?? "").trim();
    const accountNumber = (row[col("account #")] ?? "").trim();
    // "--" means Merrill couldn't resolve a ticker. Importing those would
    // lump unrelated securities together under one fake symbol.
    if (rawSymbol === "" || rawSymbol === "--") {
      warnings.push(
        `Line ${line}: skipped ${(row[descriptionCol] ?? "a security").trim()} (no ticker).`,
      );
      return;
    }
    if (MERRILL_SWEEP_SYMBOLS.has(rawSymbol)) {
      warnings.push(
        `Line ${line}: skipped ${rawSymbol} (sweep cash, already in the account's cash).`,
      );
      return;
    }
    if (accountNumber === "") {
      warnings.push(`Line ${line}: skipped ${rawSymbol} (no account number).`);
      return;
    }
    const purchaseDate = toIsoDate(row[col("acquisition date")]);
    const quantity = Number((row[col("quantity")] ?? "").replace(/,/g, ""));
    const unitCost = moneyToCents(row[col("unit cost ($)")]);
    const costBasis = moneyToCents(row[col("cost basis ($)")]);
    if (
      purchaseDate === null ||
      !Number.isFinite(quantity) ||
      quantity <= 0 ||
      unitCost === null ||
      costBasis === null
    ) {
      warnings.push(`Line ${line}: skipped ${rawSymbol} (missing lot details).`);
      return;
    }
    const value = moneyToCents(row[col("value ($)")]);
    const term = (row[col("short/long")] ?? "").toLowerCase();
    const taxTerm = term.includes("long")
      ? ("long" as const)
      : term.includes("short")
        ? ("short" as const)
        : undefined;
    const account = accounts.get(accountNumber) ?? {
      nickname: (row[nicknameCol] ?? "").trim(),
      lots: [],
    };
    account.lots.push({
      ticker: MERRILL_TICKER_MAP[rawSymbol] ?? rawSymbol,
      purchase_date: purchaseDate,
      quantity,
      unit_cost_cents: unitCost,
      cost_basis_cents: costBasis,
      ...(value !== null && { value_cents: value }),
      ...(taxTerm !== undefined && { tax_term: taxTerm }),
    });
    accounts.set(accountNumber, account);
  });

  return {
    as_of_date: asOfDate,
    accounts: [...accounts].map(([external_id, a]) => ({
      external_id,
      nickname: a.nickname,
      lots: a.lots,
    })),
    warnings,
  };
}

export interface MerrillAccountBalances {
  readonly external_id: string;
  readonly name: string;
  readonly cash_balance_cents: number;
  readonly margin_balance_cents: number;
}

export interface ParsedMerrillBalances {
  readonly as_of_date: string;
  readonly accounts: readonly MerrillAccountBalances[];
  readonly warnings: readonly string[];
}

const MERRILL_ACCOUNT_HEADER = /^(.+?)\s+([A-Z0-9]+-[A-Z0-9]+)$/;

interface BalancesBlock {
  readonly external_id: string;
  readonly name: string;
  reportedTotal: number | null;
  valueColumn: number;
  hasBalances: boolean;
  unreadable: boolean;
  positions: number;
  cash: number;
  margin: number;
}

/** Allows for rounding in Merrill's own account totals. */
const BALANCE_TOLERANCE_CENTS = 5;

function reconciles(block: BalancesBlock): boolean {
  return (
    block.hasBalances &&
    !block.unreadable &&
    block.reportedTotal !== null &&
    Math.abs(
      block.positions + block.cash + block.margin - block.reportedTotal,
    ) <= BALANCE_TOLERANCE_CENTS
  );
}

/** The Value column, or a row's only dollar amount when that cell is empty. */
function balanceCents(row: readonly string[], valueColumn: number): number | null {
  const inColumn = moneyToCents(row[valueColumn]);
  if (inColumn !== null) return inColumn;
  const amounts = row.filter((cell) => cell.includes("$")).map(moneyToCents);
  return amounts.length === 1 ? (amounts[0] ?? null) : null;
}

/**
 * Reads cash and margin from Merrill's Holdings export, which has a block
 * per account ending in a Balances section. Merrill moves the columns
 * between layouts, so each block finds Value from its own header row.
 *
 * Some exports leave out positions or the margin row, which would read as
 * "no margin loan". So an account only counts when its positions, cash and
 * margin add up to the total Merrill reports for it.
 */
export function parseMerrillBalancesCsv(text: string): ParsedMerrillBalances {
  const blocks: BalancesBlock[] = [];
  let asOfDate: string | null = null;

  for (const rawRow of parseCsv(text)) {
    const row = rawRow.map((cell) => cell.trim());
    const label = row.find((cell) => cell !== "") ?? "";
    const exported = /^Exported on:\s*(\S+)/.exec(label);
    if (exported) {
      asOfDate = toIsoDate(exported[1]);
      continue;
    }
    const header =
      row[1] === "Value" ? MERRILL_ACCOUNT_HEADER.exec(row[0] ?? "") : null;
    if (header) {
      blocks.push({
        name: header[1]!,
        external_id: header[2]!,
        reportedTotal: null,
        valueColumn: -1,
        hasBalances: false,
        unreadable: false,
        positions: 0,
        cash: 0,
        margin: 0,
      });
      continue;
    }
    const block = blocks.at(-1);
    if (!block) continue;
    if (block.reportedTotal === null) {
      block.reportedTotal = row[0] === "" ? moneyToCents(row[1]) : null;
    } else if (row.includes("Symbol")) {
      block.valueColumn = row.indexOf("Value");
    } else if (label === "Balances") {
      block.hasBalances = true;
    } else if (!block.hasBalances) {
      const isDetailRow = row[0] === "" && row[1] === "";
      if (!isDetailRow && !/total/i.test(label)) {
        block.positions += moneyToCents(row[block.valueColumn]) ?? 0;
      }
    } else {
      const isCash =
        label.startsWith("Money accounts") ||
        label === "Cash balance" ||
        label === "Pending activity";
      if (!isCash && label !== "Margin balance") continue;
      const cents = balanceCents(row, block.valueColumn);
      if (cents === null) block.unreadable = true;
      else if (isCash) block.cash += cents;
      else block.margin += cents;
    }
  }

  const readable = blocks.filter(reconciles);
  if (readable.length === 0 || asOfDate === null) {
    throw new Error(
      "This file doesn't have complete balances. Use the Merrill Holdings export named ExportData, with all accounts selected.",
    );
  }
  return {
    as_of_date: asOfDate,
    accounts: readable.map((b) => ({
      external_id: b.external_id,
      name: b.name,
      cash_balance_cents: b.cash,
      margin_balance_cents: b.margin,
    })),
    warnings: blocks
      .filter((b) => !readable.includes(b))
      .map(
        (b) =>
          `${b.name} ${b.external_id}: skipped, its balances don't add up to Merrill's total.`,
      ),
  };
}
